import { v1HttpClient } from './index.js'
import { HttpMethodEnum } from '@ipevo-rd/service-web-utils'
import {
	NchanConnectReq,
	SessionCreateRes,
	SessionHistoryReq,
	SessionMP4Req,
	SessionsEventsRes,
	SessionsReq,
	SessionsTTSReq,
	SessionSummaryReq,
} from './dto/SessionsDTO.js'
import { getViewerAPIUrl } from '../api.js'
import { ApiVersionEnum } from '../enums/ApiVersionEnum.js'

/**
 * Sessions
 */
export const Sessions = {
    /**
     * Admin
     */
    Admin: {
        /**
         * Admin 取得Sessions
         * @param params {unknown} 參數
         * @return {Promise<unknown>}
         */
        get: (params: unknown): Promise<unknown> =>
            v1HttpClient.request({
                url: '/channels',
                method: HttpMethodEnum.GET,
                params,
            }),
        /**
         * Admin 刪除Sessions
         * @param params {SessionsReq} 參數
         * @return {Promise<unknown>}
         */
        del: (params: SessionsReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/stream/${params.session_id}`,
                method: HttpMethodEnum.DELETE,
            }),
    },
    /**
     * User
     */
    User: {
        /**
         * 建立新的Session
         * @param data {SessionCreateRes} 資料
         * @return {Promise<unknown>}
         */
        create: (data: SessionCreateRes): Promise<unknown> =>
            v1HttpClient.request({
                url: '/channels',
                method: HttpMethodEnum.POST,
                data,
            }),

        /**
         * 取得Session歷史紀錄
         * @param params {SessionHistoryReq} 參數
         * @return {Promise<unknown>}
         */
        getSessionsHistory: (params: SessionHistoryReq): Promise<unknown> =>
            v1HttpClient.request({
                url: '/channels/histories',
                method: HttpMethodEnum.GET,
                params,
            }),

        /**
         * 取得Session TTS
         * @param params {SessionsTTSReq} 參數
         * @return {Promise<unknown>}
         */
        getSessionsTTS: (params: SessionsTTSReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/stream/${params.session_id}/${params.language}/${params.gender}`,
                method: HttpMethodEnum.GET,
            }),

        /**
         * 刪除Session
         * @param params {SessionsReq} 參數
         * @return {Promise<unknown>}
         */
        del: (params: SessionsReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/${params.session_id}`,
                method: HttpMethodEnum.DELETE,
            }),

        /**
         * 取得Session 資訊
         * @param params {SessionsReq} 參數
         * @return {Promise<unknown>}
         */
        getSessionsCapacity: (params: SessionsReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/${params.session_id}/capacity`,
                method: HttpMethodEnum.GET,
            }),

        /**
         * 取得Session Events
         * @param params {SessionsEventsRes} 參數
         * @return {Promise<unknown>}
         */
        getSessionsEvents: (params: SessionsEventsRes): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/${params.session_id}/events`,
                method: HttpMethodEnum.GET,
                params,
            }),

        /**
         * 設定Session 評分
         * @param params {SessionsReq} 參數
         * @return {Promise<unknown>}
         */
        setRating: (params: SessionsReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/${params.session_id}/rating`,
                method: HttpMethodEnum.POST,
                data: params,
            }),

        /**
         * 取得Session Size
         * @param params {SessionsReq} 參數
         * @return {Promise<unknown>}
         */
        getSessionsSize: (params: SessionsReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `/channels/${params.session_id}/size`,
                method: HttpMethodEnum.GET,
            }),

		/**
		 * 建立 nchan SSE 訂閱連線
		 * - URL 沿用既有 viewer SSE base（cutover 時 DNS 重綁到 nchan，前端不變）
		 * - nchan 不需要 token（auth_internal hook 查 Redis channel 存在即放行）
		 * - viewer_id 帶入讓 sub / unsub hook 計算 unique viewer 數
		 * - last_event_id 帶入時，nchan 從 buffer 補發斷線期間訊息（Last-Event-ID 語義）
		 * @param params {NchanConnectReq} channel_id、viewer_id，以及可選的 last_event_id
		 * @return {EventSource} 已建立連線的 EventSource
		 */
		connectNchan: (params: NchanConnectReq): EventSource => {
			// 組 query string
			const query = new URLSearchParams({ viewer_id: params.viewer_id })
			// 重連時帶最後 event id，nchan 依此從 buffer 補發斷線期間訊息（Last-Event-ID 語義）
			if (params.last_event_id) {
				query.set('last_event_id', params.last_event_id)
			}
			return new EventSource(
				`${getViewerAPIUrl(ApiVersionEnum.V1)}/channels/${params.channel_id}/events?${query.toString()}`,
			)
		},

		/**
		 * 更新 viewer 存活 timestamp，防止 ZSET score 過期被剔除計數
		 *
		 * @param channelId {string} 頻道 UUID
		 * @param viewerId {string} viewer 穩定識別碼
		 * @returns {Promise<void>}
		 *
		 * @example
		 * await Sessions.User.renewViewer(channelId, viewerId)
		 */
		renewViewer: (channelId: string, viewerId: string): Promise<void> => {
			// 組 query string
			const query = new URLSearchParams({ viewer_id: viewerId })
			return fetch(
				`${getViewerAPIUrl(ApiVersionEnum.V1)}/channels/${channelId}/viewers/renew?${query.toString()}`,
				{ method: 'POST' },
			).then(() => undefined)
		},

        /**
         * Get MP4 Url
         * @param params {SessionMP4Req}
         * @return {Promise<unknown>}
         */
        getSessionMP4: (params: SessionMP4Req): Promise<unknown> =>
            v1HttpClient.request({
                url: `channels/stream/${params.session_id}/${params.language}/${params.gender}/${params.message_id}`,
                method: HttpMethodEnum.GET,
            }),
        /**
         * Get Session Summary
         * @param params {SessionSummaryReq}
         * @return {Promise<unknown>}
         */
        getSessionSummary: (params: SessionSummaryReq): Promise<unknown> =>
            v1HttpClient.request({
                url: `channels/summary`,
                method: HttpMethodEnum.GET,
                params,
            }),
    },
}
